/** @jsxImportSource @emotion/react */
import React, { Fragment, useState } from 'react';
import { CSSObject } from '@emotion/react';
import Title from '../../components/title';
import Input from '../../components/input';
import Clickable from '../../components/clickable';
import useHttpCall from '../../hooks/useHttpCall';
import useLanguage from '../../hooks/useLanguage';
import RequestError from '../../utils/requestError';
import { logWarning } from '../../utils/remoteLogger';

interface IResetPasswordView {
  onSuccess: () => void;
}

// The backend reports why a password was rejected as a plain-string code in
// the response body. `request` prefers response.statusText over a string
// body, so err.message is an unhelpful "Bad Request" — read the body instead.
const PASSWORD_ERROR_KEYS: Record<string, string> = {
  invalid_password: 'reset_password_error_invalid_password',
  invalid_password_candidate: 'reset_password_error_invalid_password_candidate',
  too_long_password_candidate:
    'reset_password_error_too_long_password_candidate',
  compromised_password_candidate:
    'reset_password_error_compromised_password_candidate',
  too_many_attempts: 'reset_password_error_too_many_attempts',
  invalid_country: 'reset_password_error_invalid_country',
};

const getErrorCode = (err: RequestError): string | undefined => {
  if (typeof err.body === 'string') {
    return err.body.trim();
  }

  if (err.body && typeof err.body === 'object') {
    const { error, message } = err.body as { error?: string; message?: string };
    return error || message;
  }

  return undefined;
};

const ResetPasswordView = ({ onSuccess }: IResetPasswordView) => {
  const handleHttpCall = useHttpCall();
  const language = useLanguage();

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const passwordsMismatch =
    !!confirmPassword && newPassword !== confirmPassword;
  const canSubmit =
    !!newPassword &&
    !!confirmPassword &&
    newPassword === confirmPassword &&
    !isLoading;

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();

    if (!canSubmit) {
      return;
    }

    setIsLoading(true);
    setError(null);

    handleHttpCall({
      method: 'POST',
      url: '/api/user/fr/magic-link/reset-password',
      body: { password: newPassword },
    })
      .then(() => {
        onSuccess();
      })
      .catch((err: RequestError) => {
        const code = getErrorCode(err);
        const errorKey = code && PASSWORD_ERROR_KEYS[code];

        if (code && !errorKey) {
          logWarning('resetPassword', `Unmapped password reset error: ${code}`);
        }

        setError(language.get(errorKey || 'reset_password_generic_error'));
        setIsLoading(false);
      });
  };

  const SECTION_STYLE: CSSObject = {
    marginBottom: 32,
  };

  return (
    <Fragment>
      <div css={{ padding: '20px 20px 40px 20px' }}>
        <section css={SECTION_STYLE}>
          <Title title={language.get('reset_password')} large />
        </section>

        <form onSubmit={handleSubmit} css={SECTION_STYLE}>
          <Input
            label={language.get('reset_password_new_password_label')}
            type='password'
            autoComplete='new-password'
            value={newPassword}
            onChange={(e) => setNewPassword(e.currentTarget.value)}
            css={{ marginBottom: 20 }}
          />

          <Input
            label={language.get('reset_password_confirm_password_label')}
            type='password'
            autoComplete='new-password'
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.currentTarget.value)}
            css={{ marginBottom: 20 }}
          />

          {passwordsMismatch && (
            <div css={{ color: 'red', marginBottom: 20 }}>
              {language.get('reset_password_mismatch_error')}
            </div>
          )}

          <Clickable styleAs='button' renderAs='button' disabled={!canSubmit}>
            {isLoading
              ? language.get('reset_password_saving_button')
              : language.get('reset_password_submit_button')}
          </Clickable>
        </form>

        {error && (
          <section css={SECTION_STYLE}>
            <div css={{ color: 'red' }}>{error}</div>
          </section>
        )}
      </div>
    </Fragment>
  );
};

export default ResetPasswordView;
